import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { GoogleAuthProvider, OAuthProvider, signInWithPopup } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Box, Typography, CircularProgress, Button, Alert, Divider } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PaymentIcon from '@mui/icons-material/Payment';

const gold = '#C9A84C';
const dark = '#0D1E35';
const googleProvider = new GoogleAuthProvider();
const appleProvider  = new OAuthProvider('apple.com');

function fmt(cents) { return `$${(cents / 100).toFixed(2)}`; }

export default function TournamentPay() {
  const [searchParams] = useSearchParams();
  const regId  = searchParams.get('reg_id');
  const isPaid = searchParams.get('paid') === '1';

  const [user, setUser]       = useState(undefined);
  const [reg, setReg]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying]   = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState(isPaid);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => setUser(u || null));
    return unsub;
  }, []);

  useEffect(() => {
    if (!regId) { setLoading(false); return; }
    getDoc(doc(db, 'registrations', regId))
      .then(snap => {
        if (snap.exists()) setReg({ id: snap.id, ...snap.data() });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [regId]);

  const handleSignIn = async (provider) => {
    try { await signInWithPopup(auth, provider); }
    catch { setError('Sign-in failed. Please try again.'); }
  };

  const handlePay = async () => {
    setPaying(true); setError('');
    try {
      const functions = getFunctions();
      const createSession = httpsCallable(functions, 'createTournamentPaymentSession');
      const result = await createSession({ regId });
      window.location.href = result.data.url;
    } catch (e) {
      setError(e.message || 'Failed to start payment. Please try again.');
      setPaying(false);
    }
  };

  const Wrap = ({ children }) => (
    <Box sx={{ background: dark, minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
      <Box sx={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '16px', p: { xs: 3, sm: 5 }, maxWidth: 460, width: '100%', textAlign: 'center' }}>
        <img src="/torch_logo.png" alt="Torch" style={{ width: 50, marginBottom: 12 }} />
        <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.6rem', letterSpacing: '3px', color: 'white', mb: 0.5 }}>
          TORCH BEACH VB
        </Typography>
        {children}
      </Box>
    </Box>
  );

  if (!regId) return (
    <Wrap>
      <Alert severity="error" sx={{ mt: 2 }}>Invalid payment link. Please use the link from your confirmation email.</Alert>
    </Wrap>
  );

  if (loading || user === undefined) return (
    <Wrap><CircularProgress sx={{ color: gold, mt: 3 }} /></Wrap>
  );

  if (!reg) return (
    <Wrap>
      <Alert severity="error" sx={{ mt: 2 }}>Registration not found. Please contact us at info@torchvolleyball.com.</Alert>
    </Wrap>
  );

  if (reg.status === 'cancelled') return (
    <Wrap>
      <Alert severity="warning" sx={{ mt: 2 }}>This registration has been cancelled.</Alert>
    </Wrap>
  );

  // Already fully paid
  if (reg.payment_status === 'paid' || success) return (
    <Wrap>
      <CheckCircleOutlineIcon sx={{ fontSize: 56, color: gold, mt: 2, mb: 1 }} />
      <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.2rem', mb: 0.5 }}>Payment Complete!</Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', mb: 2 }}>
        Your registration for {reg.tournament_name} is paid in full.
      </Typography>
      <Box sx={{ p: 2, background: 'rgba(255,255,255,0.05)', borderRadius: 2, textAlign: 'left' }}>
        <Row label="Tournament" value={reg.tournament_name} />
        <Row label="Division" value={reg.division} />
        <Row label="Player" value={`${reg.first_name} ${reg.last_name}`} />
        {reg.partner_name && <Row label="Partner" value={reg.partner_name} />}
        <Row label="Amount Paid" value={fmt(reg.amount_paid || 0)} />
      </Box>
    </Wrap>
  );

  // Needs sign-in (optional but shows their info)
  const alreadyPaidCents = reg.amount_paid || 0;
  const isHalfPaid = reg.payment_status === 'half_paid';

  return (
    <Wrap>
      <Typography sx={{ color: gold, fontWeight: 700, fontSize: '1rem', mt: 2, mb: 0.5 }}>
        {isHalfPaid ? 'Complete Your Payment' : 'Pay Registration Balance'}
      </Typography>

      {/* Registration summary */}
      <Box sx={{ mt: 2, mb: 3, p: 2, background: 'rgba(255,255,255,0.05)', borderRadius: 2, textAlign: 'left' }}>
        <Row label="Tournament" value={reg.tournament_name} />
        <Row label="Division" value={reg.division} />
        <Row label="Player" value={`${reg.first_name} ${reg.last_name}`} />
        {reg.partner_name && <Row label="Partner" value={reg.partner_name} />}
        <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />
        {alreadyPaidCents > 0 && <Row label="Already Paid" value={fmt(alreadyPaidCents)} muted />}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>{error}</Alert>}

      {!user ? (
        <Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', mb: 2 }}>
            Sign in to verify your identity, or continue as guest to pay directly.
          </Typography>
          <Button fullWidth variant="contained" onClick={handlePay} disabled={paying}
            startIcon={paying ? <CircularProgress size={16} sx={{ color: dark }} /> : <PaymentIcon />}
            sx={{ mb: 1.5, background: gold, color: dark, fontWeight: 700, py: 1.4, '&:hover': { background: '#e0be6a' } }}>
            {paying ? 'Redirecting...' : 'Pay as Guest'}
          </Button>
          <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.1)' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.72rem' }}>or sign in first</Typography>
          </Divider>
          <Button fullWidth variant="outlined" onClick={() => handleSignIn(googleProvider)}
            sx={{ mb: 1, backgroundColor: 'white', color: '#333', border: 'none', fontWeight: 700, '&:hover': { backgroundColor: '#f0f0f0' } }}>
            <img src="https://developers.google.com/identity/images/g-logo.png" alt="" style={{ width: 18, marginRight: 8 }} />
            Sign in with Google
          </Button>
          <Button fullWidth variant="contained" onClick={() => handleSignIn(appleProvider)}
            sx={{ backgroundColor: 'black', color: 'white', fontWeight: 700, '&:hover': { backgroundColor: '#222' } }}>
            🍎 Sign in with Apple
          </Button>
        </Box>
      ) : (
        <Button fullWidth variant="contained" onClick={handlePay} disabled={paying}
          startIcon={paying ? <CircularProgress size={16} sx={{ color: dark }} /> : <PaymentIcon />}
          sx={{ background: gold, color: dark, fontWeight: 700, fontSize: '1rem', py: 1.5, borderRadius: '10px', '&:hover': { background: '#e0be6a' } }}>
          {paying ? 'Redirecting to Stripe...' : 'Pay Remaining Balance'}
        </Button>
      )}
      <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.72rem', mt: 1.5 }}>
        Secure checkout via Stripe
      </Typography>
    </Wrap>
  );
}

function Row({ label, value, muted }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
      <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>{label}</Typography>
      <Typography sx={{ color: muted ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.85)', fontSize: '0.8rem', fontWeight: muted ? 400 : 600 }}>{value}</Typography>
    </Box>
  );
}
